// src/components/RegionalPlansSection.js
import React, { useEffect, useMemo, useState } from 'react';
import CachedImg from './CachedImg';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import { Globe, MapPin } from 'lucide-react';
import { useTranslation } from '../useTranslation';

// shared slug helper (matches the page)
export const slugify = (s = '') =>
  (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

const RegionalPlansSection = () => {
  const { t } = useTranslation(); // Add this line
  const [regionalPlans, setRegionalPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'regional_plans'),
      (snap) => {
        const plans = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRegionalPlans(plans);
        setLoading(false);
        setErr(null);
      },
      (e) => {
        console.error('regional_plans listener error', e);
        setErr('Failed to load regional plans');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Group by plan_id (aka region group), aggregate counts and min price
  const groups = useMemo(() => {
    const byId = {};
    for (const p of regionalPlans) {
      const key = p.plan_id || p.plan_name || 'Unknown';
      if (!byId[key]) {
        byId[key] = {
          plan_id: key,
          plan_name: p.plan_name || key,
          plan_region: p.plan_region || key,
          image_url: p.image_url || '',
          countries_max: p.countries_count || 0,
          plans_count: 0,
          min_price: Number.isFinite(p.price) ? p.price : null,
        };
      }
      const g = byId[key];
      g.plans_count += 1;
      if (p.countries_count && p.countries_count > g.countries_max) {
        g.countries_max = p.countries_count;
      }
      if (Number.isFinite(p.price)) {
        g.min_price = g.min_price == null ? p.price : Math.min(g.min_price, p.price);
      }
      if (!g.image_url && p.image_url) g.image_url = p.image_url;
    }
    return Object.values(byId).sort(
      (a, b) => b.countries_max - a.countries_max || a.plan_name.localeCompare(b.plan_name)
    );
  }, [regionalPlans]);

  return (
    <section className="relative py-20 sm:px-32 lg:py-24 bg-white">
      {/* subtle top divider glow */}
      <div className="pointer-events-none absolute inset-x-0 -top-12 h-24 bg-gradient-to-b from-purple-100/40 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* header */}
        <div className="mb-10 flex items-center justify-between">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-purple-200">
              <Globe className="w-3.5 h-3.5" />
              {t('regionalPlans.multiCountryCoverage')}
            </span>
            <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">
              {t('regionalPlans.title')}
            </h2>
            <p className="text-gray-600 mt-2">
              {t('regionalPlans.subtitle')}
            </p>
          </div>
        </div>

        {/* loading */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="relative h-64 rounded-3xl border border-gray-200/70 bg-gray-50 overflow-hidden"
              >
                <div className="h-36 bg-gradient-to-r from-gray-200 to-gray-100" />
                <div className="p-6 space-y-3">
                  <div className="h-5 w-40 bg-gray-200 rounded-md" />
                  <div className="h-4 w-24 bg-gray-200 rounded-md" />
                  <div className="h-10 w-full bg-gray-200 rounded-lg" />
                </div>
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-[linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent)]" />
              </div>
            ))}
          </div>
        ) : err ? (
          <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
            {err}
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-16">
            <Globe className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">{t('regionalPlans.noPlansAvailable')}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {groups.map((g) => {
              const slug = slugify(g.plan_id);
              return (
                <Link
                  key={g.plan_id}
                  to={`/regional/${slug}`}
                  className={[
                    'group rounded-3xl overflow-hidden border border-gray-200/80 bg-white',
                    'shadow-sm hover:shadow-xl hover:border-gray-300/80 transition-all duration-300',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400'
                  ].join(' ')}
                >
                  {/* media */}
                  <div className="relative h-44">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600" />
                    {g.image_url ? (
                      <CachedImg
                        src={g.image_url}
                        alt={g.plan_name}
                        className="absolute inset-0 w-full h-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-[1.03]"
                        keyPrefix="airvoy:regional"
                        ttlDays={14}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-white/70">
                        <Globe className="w-10 h-10" />
                      </div>
                    )}

                    {/* legibility layers */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(0,0,0,.35))]" />
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/45 to-transparent" />

                    {/* title */}
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-white text-xl font-semibold drop-shadow-sm">
                        {g.plan_name}
                      </h3>
                    </div>

                    {/* price badge */}
                    <div className="absolute top-3 right-3">
                      <span className="rounded-2xl bg-white/90 backdrop-blur px-3 py-1.5 shadow-md border border-white text-sm font-semibold text-gray-900">
                        {g.min_price != null ? `${t('regionalPlans.from')} $${g.min_price.toFixed(2)}` : t('regionalPlans.seePlans')}
                      </span>
                    </div>
                  </div>

                  {/* body */}
                  <div className="p-6 space-y-3">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-gray-100 ring-1 ring-gray-200">
                          <MapPin className="w-4 h-4 text-gray-700" />
                        </span>
                        {g.countries_max} {t('regionalPlans.countries')}
                      </span>
                      <span className="font-medium">{g.plans_count} {t('regionalPlans.plans')}</span>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <span className="text-gray-900 font-semibold">
                        {g.min_price != null ? `${t('regionalPlans.startingFrom')} $${g.min_price.toFixed(2)}` : t('regionalPlans.browseOptions')}
                      </span>
                      <span className="text-purple-700 font-semibold group-hover:underline">
                        {t('regionalPlans.viewPlans')} →
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default RegionalPlansSection;